import { createColumnHelper } from '@tanstack/react-table';
import { TableLink } from '@monorepo/base/src/components/table/table-link/table-link';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import { TableSelectionCell, TableSelectionHeader } from '@monorepo/base/src/components/table/table-selection-cell/table-selection-cell';
import { isSubRow } from '@monorepo/base/src/components/table/table';
import { IMergedHeadersReturn } from './index.headers';
import { IAdminxStore } from '../../stores';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useRoute } from '../tools/use-route';
import { useEffect } from 'react';
import { BidderModelTableModel } from '../../models/bidder-model.model';
import routes from '../../routes/index.routes';

const columnHelper = createColumnHelper<BidderModelTableModel>();

export interface IUseBidderModelsHeadersOptions {
	/** When true, selection column has no header (no "select all" checkbox). Use with Table `singleRowSelection`. */
	singleRowSelection?: boolean;
}

export const useBidderModelsHeaders = (options: IUseBidderModelsHeadersOptions = {}): IMergedHeadersReturn<BidderModelTableModel> => {
	const { singleRowSelection: singleRowSelectionMode = false } = options;
	const { settingsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;

	useEffect(() => {
		if (tableStore && Object.keys(tableStore.getColumnVisibility()).length === 0) {
			tableStore.setDefaultColumnVisibility({
				selection: true,
				modelName: true,
				realAdvertiserId: true,
				campaigns: true,
				lastSuccessfulBuild: true,
				shouldBuild: true,
				status: true,
			});

			tableStore.setColumnVisibility(tableStore.getDefaultColumnVisibility());

			if (tableStore.getSortingBy().length === 0) {
				tableStore.setSortingBy([{ id: 'modelName', desc: false }]);
			}
		}
	}, [tableStore]);

	return {
		columns: [
			columnHelper.display({
				id: 'selection',
				enableHiding: false,
				header: singleRowSelectionMode ? () => null : headerProps => <TableSelectionHeader headerProps={headerProps} />,
				cell: cellProps => {
					const { row } = cellProps;
					if (isSubRow(row)) {
						return <span></span>;
					}
					return <TableSelectionCell cellProps={cellProps} />;
				},
			}),
			columnHelper.accessor('modelName', {
				header: 'Model Name',
				cell: ({ row }) => (
					<TableLink
						toEdit={routes.bidder.editModel(encodeURIComponent(row.getValue('modelName')))}
						toEditState={{ isBlockRequest: true } as BetweenPagesStateProps}>
						{row.getValue('modelName')}
					</TableLink>
				),
			}),
			columnHelper.accessor('realAdvertiserId', { header: 'Real Advertiser ID' }),
			columnHelper.accessor('campaigns', { header: 'Campaigns' }),
			columnHelper.accessor('lastSuccessfulBuild', { header: 'Last Successful Build' }),
			columnHelper.accessor('shouldBuild', {
				header: 'Should Build',
				cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
			}),
			columnHelper.accessor('status', { header: 'Status' }),
		],
	};
};
